import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';
import { HierarchicalAccordion } from '../../../../common/HierarchicalAccordion';
import { HierarchicalAccordionItem } from '../../../../common/HierarchicalAccordionItem';
import { type SelectOption } from '../../../../common/inputs/SelectInput';

interface StyleEditorSectionProps {
    layer: LayerBase & LayerStyleProperties;
    updateLayerStyle: (layerId: string, propertyName: keyof LayerStyleProperties, value: StyleValue) => void;
    isDisabled: boolean;
    availablePropertyKeys?: string[]; // Aggiunta prop
}

export const SymbolLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
    if (layer.maplibreLayerType !== 'symbol') return null;
    const { id: layerId } = layer;
    const uniqueIdPrefix = `symbol-layout-${layerId}`;

    const textFieldOptions: SelectOption[] = React.useMemo(() => (
        layer.availablePropertyKeys
            ? layer.availablePropertyKeys.map(key => ({ value: key, label: key }))
            : []
    ), [layer.availablePropertyKeys]);
 
    // Gestione di currentValue se layer.textField è un'espressione ['get', fieldName]
    // Questo aiuta il SelectInput a trovare il valore corrente corretto nelle opzioni,
    // se layer.textField è un'espressione semplice per ottenere una proprietà.
    let displayValueForTextField = layer.textField;
    if (
        Array.isArray(layer.textField) &&
        layer.textField.length === 2 &&
        layer.textField[0] === 'get' &&
        typeof layer.textField[1] === 'string'
    ) {
        displayValueForTextField = layer.textField[1];
    }

    return (
        <HierarchicalAccordion style={{ width: '100%' }} defaultOpenId={`${uniqueIdPrefix}-general`}>
            <HierarchicalAccordionItem title="Generali" level={2} id={`${uniqueIdPrefix}-general`}>
                <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                    <StylePropertyInput
                        label="Symbol Placement"
                        propertyName="symbolPlacement"
                        propertyType="select"
                        currentValue={layer.symbolPlacement}
                        selectOptions={[
                            { value: 'point', label: 'Point' },
                            { value: 'line', label: 'Line' },
                            { value: 'line-center', label: 'Line Center' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Symbol Spacing"
                        propertyName="symbolSpacing"
                        propertyType="number"
                        currentValue={layer.symbolSpacing}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 1, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Symbol Avoid Edges"
                        propertyName="symbolAvoidEdges"
                        propertyType="boolean"
                        currentValue={layer.symbolAvoidEdges}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Symbol Sort Key"
                        propertyName="symbolSortKey"
                        propertyType="number"
                        currentValue={layer.symbolSortKey}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Symbol Z Order"
                        propertyName="symbolZOrder"
                        propertyType="select"
                        currentValue={layer.symbolZOrder}
                        selectOptions={[
                            { value: 'auto', label: 'Auto' },
                            { value: 'viewport-y', label: 'Viewport Y' },
                            { value: 'source', label: 'Source' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Visibility"
                        propertyName="visibility"
                        propertyType="select"
                        currentValue={layer.visibility ?? 'visible'}
                        selectOptions={[
                            { value: 'visible', label: 'Visible' },
                            { value: 'none', label: 'None' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                </div>
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem title="Icona (Layout)" level={2} id={`${uniqueIdPrefix}-icon`}>
                <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                    <StylePropertyInput
                        label="Icon Image"
                        propertyName="iconImage"
                        propertyType="string"
                        currentValue={layer.iconImage}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Size"
                        propertyName="iconSize"
                        propertyType="number"
                        currentValue={layer.iconSize}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Rotate"
                        propertyName="iconRotate"
                        propertyType="number"
                        currentValue={layer.iconRotate}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Offset [x, y]"
                        propertyName="iconOffset"
                        propertyType="text"
                        currentValue={JSON.stringify(layer.iconOffset ?? [0,0])}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                const parsed = JSON.parse(val as string);
                                if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
                                    updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                                }
                            } catch { /* gestisci errore parsing */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Anchor"
                        propertyName="iconAnchor"
                        propertyType="select"
                        currentValue={layer.iconAnchor}
                        selectOptions={[
                            { value: 'center', label: 'Center' }, { value: 'left', label: 'Left' },
                            { value: 'right', label: 'Right' }, { value: 'top', label: 'Top' },
                            { value: 'bottom', label: 'Bottom' }, { value: 'top-left', label: 'Top Left' },
                            { value: 'top-right', label: 'Top Right' }, { value: 'bottom-left', label: 'Bottom Left' },
                            { value: 'bottom-right', label: 'Bottom Right' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Allow Overlap"
                        propertyName="iconAllowOverlap"
                        propertyType="boolean"
                        currentValue={layer.iconAllowOverlap}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Ignore Placement"
                        propertyName="iconIgnorePlacement"
                        propertyType="boolean"
                        currentValue={layer.iconIgnorePlacement}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Optional"
                        propertyName="iconOptional"
                        propertyType="boolean"
                        currentValue={layer.iconOptional}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Rotation Alignment"
                        propertyName="iconRotationAlignment"
                        propertyType="select"
                        currentValue={layer.iconRotationAlignment}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                            { value: 'auto', label: 'Auto' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Text Fit"
                        propertyName="iconTextFit"
                        propertyType="select"
                        currentValue={layer.iconTextFit}
                        selectOptions={[
                            { value: 'none', label: 'None' }, { value: 'width', label: 'Width' },
                            { value: 'height', label: 'Height' }, { value: 'both', label: 'Both' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Text Fit Padding [t,r,b,l]"
                        propertyName="iconTextFitPadding"
                        propertyType="text"
                        currentValue={JSON.stringify(layer.iconTextFitPadding ?? [0,0,0,0])}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                const parsed = JSON.parse(val as string);
                                if (Array.isArray(parsed) && parsed.length === 4 && parsed.every(n => typeof n === 'number')) {
                                    updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                                }
                            } catch { /* gestisci errore parsing */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Padding"
                        propertyName="iconPadding"
                        propertyType="number"
                        currentValue={layer.iconPadding}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Keep Upright"
                        propertyName="iconKeepUpright"
                        propertyType="boolean"
                        currentValue={layer.iconKeepUpright}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Pitch Alignment"
                        propertyName="iconPitchAlignment"
                        propertyType="select"
                        currentValue={layer.iconPitchAlignment}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                            { value: 'auto', label: 'Auto' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                </div>
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem title="Testo (Layout)" level={2} id={`${uniqueIdPrefix}-text`}>
                <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                    <StylePropertyInput
                        label="Text Field"
                        propertyName="textField"
                        propertyType="select"
                        currentValue={displayValueForTextField}
                        selectOptions={textFieldOptions}
                        onStyleChange={(layerId, propertyName, value) => {
                            if (propertyName === 'textField' && typeof value === 'string') {
                                updateLayerStyle(layerId, propertyName, ['get', value]);
                            } else {
                                updateLayerStyle(layerId, propertyName, value);
                            }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Font"
                        propertyName="textFont"
                        propertyType="select"
                        currentValue={Array.isArray(layer.textFont) && layer.textFont.length > 0 ? layer.textFont[0] : ''}
                        selectOptions={[
                            { value: 'Open Sans Regular', label: 'Open Sans Regular' },
                            { value: 'Arial Unicode MS Regular', label: 'Arial Unicode MS Regular' },
                            { value: 'Noto Sans Regular', label: 'Noto Sans Regular' },
                            { value: 'Times New Roman Regular', label: 'Times New Roman Regular' },
                        ]}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            // MapLibre si aspetta un array di stringhe per text-font.
                            // StylePropertyInput con propertyType="select" dovrebbe passare il valore selezionato (stringa).
                            // Quindi, wrappiamo il valore in un array.
                            updateLayerStyle(id, prop as keyof LayerStyleProperties, val ? [val as string] : []);
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Size"
                        propertyName="textSize"
                        propertyType="number"
                        currentValue={layer.textSize}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Max Width"
                        propertyName="textMaxWidth"
                        propertyType="number"
                        currentValue={layer.textMaxWidth}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Line Height"
                        propertyName="textLineHeight"
                        propertyType="number"
                        currentValue={layer.textLineHeight}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Letter Spacing"
                        propertyName="textLetterSpacing"
                        propertyType="number"
                        currentValue={layer.textLetterSpacing}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ step: 0.01 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Justify"
                        propertyName="textJustify"
                        propertyType="select"
                        currentValue={layer.textJustify}
                        selectOptions={[
                            { value: 'auto', label: 'Auto' }, { value: 'left', label: 'Left' },
                            { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Radial Offset"
                        propertyName="textRadialOffset"
                        propertyType="number"
                        currentValue={layer.textRadialOffset}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Variable Anchor"
                        propertyName="textVariableAnchor"
                        propertyType="text"
                        currentValue={Array.isArray(layer.textVariableAnchor) ? layer.textVariableAnchor.join(', ') : ''}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            const anchors = (val as string).split(',').map(a => a.trim()).filter(a => a);
                            updateLayerStyle(id, prop as keyof LayerStyleProperties, anchors);
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                     <StylePropertyInput
                        label="Var. Anchor Offset"
                        propertyName="textVariableAnchorOffset"
                        propertyType="text" // Temporaneamente text, idealmente un editor JSON o custom
                        currentValue={layer.textVariableAnchorOffset ? JSON.stringify(layer.textVariableAnchorOffset) : ''}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                updateLayerStyle(id, prop as keyof LayerStyleProperties, val as string);
                            } catch { /* gestisci errore parsing se necessario */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true} // Questa proprietà supporta espressioni
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                        tooltip="Definisce offset specifici per text-variable-anchor. Richiede un'espressione MapLibre complessa."
                    />
                    <StylePropertyInput
                        label="Text Anchor"
                        propertyName="textAnchor"
                        propertyType="select"
                        currentValue={layer.textAnchor}
                        selectOptions={[
                            { value: 'center', label: 'Center' }, { value: 'left', label: 'Left' },
                            { value: 'right', label: 'Right' }, { value: 'top', label: 'Top' },
                            { value: 'bottom', label: 'Bottom' }, { value: 'top-left', label: 'Top Left' },
                            { value: 'top-right', label: 'Top Right' }, { value: 'bottom-left', label: 'Bottom Left' },
                            { value: 'bottom-right', label: 'Bottom Right' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Max Angle"
                        propertyName="textMaxAngle"
                        propertyType="number"
                        currentValue={layer.textMaxAngle}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, max: 360, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Rotate"
                        propertyName="textRotate"
                        propertyType="number"
                        currentValue={layer.textRotate}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Padding"
                        propertyName="textPadding"
                        propertyType="number"
                        currentValue={layer.textPadding}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Keep Upright"
                        propertyName="textKeepUpright"
                        propertyType="boolean"
                        currentValue={layer.textKeepUpright}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Transform"
                        propertyName="textTransform"
                        propertyType="select"
                        currentValue={layer.textTransform}
                        selectOptions={[
                            { value: 'none', label: 'None' }, { value: 'uppercase', label: 'Uppercase' },
                            { value: 'lowercase', label: 'Lowercase' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Offset [x, y]"
                        propertyName="textOffset"
                        propertyType="text"
                        currentValue={JSON.stringify(layer.textOffset ?? [0,0])}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                const parsed = JSON.parse(val as string);
                                if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
                                    updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                                }
                            } catch { /* gestisci errore parsing */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Allow Overlap"
                        propertyName="textAllowOverlap"
                        propertyType="boolean"
                        currentValue={layer.textAllowOverlap}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Ignore Placement"
                        propertyName="textIgnorePlacement"
                        propertyType="boolean"
                        currentValue={layer.textIgnorePlacement}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Optional"
                        propertyName="textOptional"
                        propertyType="boolean"
                        currentValue={layer.textOptional}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Rotation Alignment"
                        propertyName="textRotationAlignment"
                        propertyType="select"
                        currentValue={layer.textRotationAlignment}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                            { value: 'auto', label: 'Auto' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Pitch Alignment"
                        propertyName="textPitchAlignment"
                        propertyType="select"
                        currentValue={layer.textPitchAlignment}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                            { value: 'auto', label: 'Auto' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Writing Mode (comma sep.)"
                        propertyName="textWritingMode"
                        propertyType="text"
                        currentValue={Array.isArray(layer.textWritingMode) ? layer.textWritingMode.join(', ') : ''}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            const modes = (val as string).split(',').map(m => m.trim()).filter(m => m === 'horizontal' || m === 'vertical') as ('horizontal' | 'vertical')[];
                            updateLayerStyle(id, prop as keyof LayerStyleProperties, modes);
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                </div>
            </HierarchicalAccordionItem>
        </HierarchicalAccordion>
    );
};


export const SymbolPaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
    if (layer.maplibreLayerType !== 'symbol') return null;
    const { id: layerId } = layer;
    const uniqueIdPrefix = `symbol-paint-${layerId}`;

    return (
        <HierarchicalAccordion style={{ width: '100%' }} defaultOpenId={`${uniqueIdPrefix}-icon`}>
            <HierarchicalAccordionItem title="Icona (Paint)" level={2} id={`${uniqueIdPrefix}-icon`}>
                <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                    <StylePropertyInput
                        label="Icon Opacity"
                        propertyName="iconOpacity"
                        propertyType="range"
                        currentValue={layer.iconOpacity}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, max: 1, step: 0.01 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                        hideNumericValueDisplay={true}
                    />
                    <StylePropertyInput
                        label="Icon Color"
                        propertyName="iconColor"
                        propertyType="color"
                        currentValue={layer.iconColor}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Halo Color"
                        propertyName="iconHaloColor"
                        propertyType="color"
                        currentValue={layer.iconHaloColor}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Halo Width"
                        propertyName="iconHaloWidth"
                        propertyType="number"
                        currentValue={layer.iconHaloWidth}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Halo Blur"
                        propertyName="iconHaloBlur"
                        propertyType="number"
                        currentValue={layer.iconHaloBlur}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Translate [x, y]"
                        propertyName="iconTranslate"
                        propertyType="text"
                        currentValue={JSON.stringify(layer.iconTranslate ?? [0,0])}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                const parsed = JSON.parse(val as string);
                                if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
                                    updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                                }
                            } catch { /* gestisci errore parsing */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Icon Translate Anchor"
                        propertyName="iconTranslateAnchor"
                        propertyType="select"
                        currentValue={layer.iconTranslateAnchor}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                </div>
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem title="Testo (Paint)" level={2} id={`${uniqueIdPrefix}-text`}>
                <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
                    <StylePropertyInput
                        label="Text Opacity"
                        propertyName="textOpacity"
                        propertyType="range"
                        currentValue={layer.textOpacity}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, max: 1, step: 0.01 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                        hideNumericValueDisplay={true}
                    />
                    <StylePropertyInput
                        label="Text Color"
                        propertyName="textColor"
                        propertyType="color"
                        currentValue={layer.textColor}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Halo Color"
                        propertyName="textHaloColor"
                        propertyType="color"
                        currentValue={layer.textHaloColor}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Halo Width"
                        propertyName="textHaloWidth"
                        propertyType="number"
                        currentValue={layer.textHaloWidth}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Halo Blur"
                        propertyName="textHaloBlur"
                        propertyType="number"
                        currentValue={layer.textHaloBlur}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        numberConfig={{ min: 0, step: 0.1 }}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Translate [x, y]"
                        propertyName="textTranslate"
                        propertyType="text"
                        currentValue={JSON.stringify(layer.textTranslate ?? [0,0])}
                        onStyleChange={(id, prop, val) => {
                            if (isDisabled) return;
                            try {
                                const parsed = JSON.parse(val as string);
                                if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
                                    updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                                }
                            } catch { /* gestisci errore parsing */ }
                        }}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                    <StylePropertyInput
                        label="Text Translate Anchor"
                        propertyName="textTranslateAnchor"
                        propertyType="select"
                        currentValue={layer.textTranslateAnchor}
                        selectOptions={[
                            { value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' },
                        ]}
                        onStyleChange={updateLayerStyle}
                        layerId={layerId}
                        isExpressionPossible={true}
                        availablePropertyKeys={layer.availablePropertyKeys}
                        disabled={isDisabled}
                    />
                </div>
            </HierarchicalAccordionItem>
        </HierarchicalAccordion>
    );
};


const SymbolStyleEditor: React.FC<StyleEditorSectionProps> = () => {
    // Questo componente non renderizza più direttamente l'intera UI.
    return null;
};

export default SymbolStyleEditor;
